<script setup>
import { computed } from 'vue'

/**
 * The component's inherited properties.
 */
const props = defineProps({
    /**
     * The given load.
     *
     * @type {Object}
     */
    load: {
        type: Object,
        required: true,
    },

    /**
     * The current location of the load.
     *
     * @type {Object}
     */
    value: {
        type: Object,
        default: () => {},
    },
})

/**
 * The load's current location.
 *
 * @return {String}
 */
const currentLocation = computed(() => {
    const location = props.load?.current_location
    const name = location?.name

    return name || `${location?.geo?.latitude},${location?.geo?.longitude}`
})

/**
 * The load's drivers.
 *
 * @return {Array}
 */
const drivers = computed(() => {
    return props.load?.drivers || []
})

/**
 * The load's recorded at time.
 *
 * @return {String}
 */
const recordedAt = computed(() => {
    return props.load?.current_location?.recorded_at || '--'
})

/**
 * The current location data supplier.
 *
 * @return {String}
 */
const dataSupplier = computed(() => {
    return props.load?.current_location?.data_supplier?.name || '--'
})

/**
 * The load's trailers.
 *
 * @return {Array}
 */
const trailers = computed(() => {
    const defaultValue = '--'
    const models = props.load?.trailers || []

    return models.map((trailer) => trailer.name).join(', ') || defaultValue
})

/**
 * The load's truck name.
 *
 * @return {String}
 */
const truckName = computed(() => {
    return props.load?.truck?.name || '--'
})
</script>

<template>
    <div class="flex">
        <div class="w-48">
            <div class="text-gray-500 text-sm leading-none">Driver(s)</div>
            <div data-test="assignment-drivers">
                <span v-for="(driver, index) in drivers" :key="index">
                    {{ driver.name }}
                    <span v-if="index != drivers.length - 1"> , </span>
                </span>
            </div>
        </div>
        <div class="w-48">
            <div class="text-gray-500 text-sm leading-none">Truck</div>
            <div>{{ truckName }}</div>
        </div>
        <div class="w-48">
            <div class="text-gray-500 text-sm leading-none">Trailer(s)</div>
            {{ trailers }}
        </div>
    </div>
    <div>
        <span class="text-sm" data-test="assignment-current-location">
            {{ currentLocation }} @ {{ recordedAt }}
            <span> via {{ dataSupplier }} </span>
        </span>
    </div>
</template>
